import React, { useState, useEffect } from 'react';
import logoSrc from '../../../assets/images/logo-long-Q.png';
import { certDownload } from '../../../utils';
import { useDispatch } from 'react-redux';
import { authCertsReset } from '../../../store/API/actions';

interface Props {
    certsFilesCheck: (certs: string[]) => Promise<boolean>,
    heartbeatCheck: () => Promise<boolean>,
    serverCheck: () => Promise<void>
}
interface browserT {
    firefox: boolean,
    safari: boolean,
    edge: boolean,
    chrome: boolean,
    generic: boolean
}

const AuthCertsComponent:React.FC<Props> = ({heartbeatCheck, certsFilesCheck, serverCheck}) => {
    const dispatch = useDispatch();
    const [browser, setBrowser] = useState<browserT>({
        firefox: false,
        safari: false,
        edge: false,
        chrome: false,
        generic: false    
    });

    const onReturnToLoginPage = async () => {
        dispatch(authCertsReset());
        await serverCheck();
    };


    const clickHandle = async (cert: string) => {
        const heartbeat = await heartbeatCheck();
        const certsFiles = await certsFilesCheck([cert]);
        if (heartbeat && certsFiles) {
            certDownload(cert)
        }
    }
    

    const browserCheck = () => {
        //@ts-ignore
        const firefox = typeof InstallTrigger !== 'undefined';
        //@ts-ignore
        const safari = /constructor/i.test(window.HTMLElement) || (function (p) { return p.toString() === "[object SafariRemoteNotification]"; })(!window['safari'] || (typeof safari !== 'undefined' && safari.pushNotification));
        //@ts-ignore
        const isIE = /*@cc_on!@*/false || !!document.documentMode;
        //@ts-ignore
        const edge = !isIE && !!window.StyleMedia;
        //@ts-ignore
        const chrome = !!window.chrome && !!window.chrome.webstore;

        setBrowser({
            firefox,
            safari,
            edge,
            chrome,
            generic: !firefox && !safari && !edge && !chrome
        });
    }

    useEffect(() => {
        browserCheck();
    }, []);

    return (
        <div className='auth-form-wrapper auth-certs-wrapper'>
            <div id="onssi-logo">
                <img src={logoSrc} width="79"/>
            </div>
            <strong id="install_header">Certificates need to be installed to enable HTTPS</strong>

            {
                browser.generic && <ol>
                    <li>
                        Download the root and intermediate certificates for the certificate authority.<br/>
                        <a onClick={() => {clickHandle('root-ca.crt')}}>Download Root Certificate</a> (root-ca.crt)<br/>
                        <a onClick={() => {clickHandle('intermediate-ca.crt')}}>Download Intermediate Certificate</a> (intermediate-ca.crt)<br/>
                    </li>
                    <li>Open the Options, Settings, or Preferences for your browser.</li>
                    <li>Find the section pertaining to certificates.</li>
                    <li>Import the file root-ca.crt from the download location as a root CA certificate and authorize it to authenticate web pages.</li>
                    <li>Import the intermediate-ca.crt file as an intermediate CA certificate and authorize it to authenticate web pages.</li>
                    <li>
                        Reload the login page. If you did everything right, you should be able to login.<br/>
                        If you return to this page, try restarting your browser.<br/>
                        If you are still unable to login, contact your System Administrator for assistance.
                    </li>
                </ol>
            }

            {
               browser.chrome && <ol id="chromeInstructions">
                    <li>
                        Download the root and intermediate certificates for the certificate authority.<br/>
                        <a onClick={() => {clickHandle('root-ca.crt')}}>Download Root Certificate</a> (root-ca.crt)<br/>
                        <a onClick={() => {clickHandle('intermediate-ca.crt')}}>Download Intermediate Certificate</a> (intermediate-ca.crt)<br/>
                    </li>
                    <li>Open your browser's <b><i>Settings</i></b> page.</li>
                    <li>Select <b><i>Manage certificates</i></b> (type <b><i>certificates</i></b> in the <b><i>Search settings</i></b> input if you can't find it).</li>
                    <li>Select the <b><i>Trusted Root Certification Authorities</i></b> tab.</li>
                    <li><b><i>Import</i></b> the file <b><i>root-ca.crt</i></b> from the download location.</li>
                    <li>Select the <b><i>Intermediate Certification Authorities</i></b> tab.</li>
                    <li><b><i>Import</i></b> the <b><i>intermediate-ca.crt</i></b> from the download location.</li>
                    <li>
                        Restart your browser and return to this page. If you did everything right, you should be able to login.<br/>
                        If you are still unable to login, contact your System Administrator for assistance.
                    </li>
                </ol>
            }

            {
                browser.firefox && <ol id="firefoxInstructions">
                    <li>
                        Download the root certificate for the certificate authority.<br/>
                        <a onClick={() => {clickHandle('root-ca.crt')}}>Download Root Certificate</a> (root-ca.crt)<br/>
                    </li>
                    <li>Open your browser's <b><i>Options page</i></b>.</li>
                    <li>Select <b><i>View Certificates</i></b> (type <i><b>certificates</b></i> in the <i><b>Find in Options</b></i> input if you can't find it).</li>
                    <li><b><i>Import</i></b> the file <b><i>root-ca.crt</i></b> from the download location and authorize it to authenticate web pages.</li>
                    <li>
                        Reload the login page. If you did everything right, you should be able to login.<br/>
                        If you return to this page, try restarting your browser.<br/>
                        If you are still unable to login, contact your System Administrator for assistance.
                    </li>
                </ol>
            }
                    
            {
                browser.edge && <ol id="edgeInstructions">
                    <li>
                        Download the root and intermediate certificates for the certificate authority.<br/>
                        <a onClick={() => {clickHandle('root-ca.crt')}}>Download Root Certificate</a> (root-ca.crt)<br/>
                        <a onClick={() => {clickHandle('intermediate-ca.crt')}}>Download Intermediate Certificate</a> (intermediate-ca.crt)<br/>
                    </li>
                    <li>Open the folder where you saved the certificates.</li>
                    <li>Right-click on <b>root-ca.crt</b> and select <b>Install Certificate</b>.</li>
                    <li>Select <b>Current User</b> and click <b>Next</b>.</li>
                    <li>Select <b>Place all certificates in the following store</b> and click <b>Browse</b>.</li>
                    <li>Select <b>Trusted Root Certification Authorities</b> and click <b>OK</b>.</li>
                    <li>Click <b>Next</b>, then <b>Finish</b>, then <b>Yes</b>, and then <b>OK</b>.</li>
                    <li>Right-click on <b>intermediate-ca.crt</b> and select <b>Install Certificate</b>.</li>
                    <li>Select <b>Current User</b> and click <b>Next</b>.</li>
                    <li>Select <b>Place all certificates in the following store</b> and click <b>Browse</b>.</li>
                    <li>Select <b>Intermediate Certification Authorities</b> and click <b>OK</b>.</li>
                    <li>Click <b>Next</b>, then <b>Finish</b>, then <b>Yes</b>, and then <b>OK</b>.</li>
                    <li>
                        Reload the login page. If you did everything right, you should be able to login.<br/>
                        If you return to this page, try restarting your browser.<br/>
                        If you are still unable to login, contact your System Administrator for assistance.
                    </li>
                </ol>
            }

            {
                browser.safari && <ol id="safariInstructions">
                    <li>
                        Download the root and intermediate certificates for the certificate authority.<br/>
                        <a onClick={() => {clickHandle('root-ca.crt')}}>Download Root Certificate</a> (root-ca.crt)<br/>
                        <a onClick={() => {clickHandle('intermediate-ca.crt')}}>Download Intermediate Certificate</a> (intermediate-ca.crt)<br/>
                    </li>
                    <li>Open the <b>Keychain Access</b> application from <b>Launchpad</b>.</li>
                    <li>Navigate to <b>System</b>.</li>
                    <li>Open the <b>File</b> menu.</li>
                    <li>Select <b>Import Items</b>.</li>
                    <li>Select the <b>RootCA</b> cerificate.</li>
                    <li>Do the same with the <b>IntCA</b> certificate.</li>
                    <li>Double-click on the <b>RootCA</b> in <b>Keychain Access</b>.</li>
                    <li>Expand the <b>Trust</b> heading.</li>
                    <li>Set value of <b>When using this certificate</b> field to <b>Always Trust</b>.</li>
                    <li>
                        Reload the login page. If you did everything right, you should be able to login.<br/>
                        If you return to this page, try restarting your browser.<br/>
                        If you are still unable to login, contact your System Administrator for assistance.
                    </li>
                </ol>
            }
                    

            <div className='flex js-c'>
                <button className='auth-failed-button'
                        onClick={() => onReturnToLoginPage()}
                >
                    Back
                </button>
            </div>
        </div>
    );
};

export default AuthCertsComponent